import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Calendar, DollarSign, MessageCircle, Star, Edit, Clock, CheckCircle, User, Crown, LifeBuoy, ShoppingBag, Package, Eye, Settings, Trophy, Globe, CalendarClock, Zap, Gift, AlertTriangle, Video } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import ConversationList from '../components/messaging/ConversationList';
import MessageThread from '../components/messaging/MessageThread';
import TrainerAccountSettings from '../components/trainer/TrainerAccountSettings';
import TrainerProfileEditor from '../components/trainer/TrainerProfileEditor';
import MembershipTab from '../components/trainer/MembershipTab';
import ListingCreditsTab from '../components/trainer/ListingCreditsTab';
import SocialMediaTab from '../components/trainer/SocialMediaTab';
import AchievementsTab from '../components/trainer/AchievementsTab';
import ReferralTab from '../components/trainer/ReferralTab';
import TrainingTypeSelector from '../components/trainer/TrainingTypeSelector';
import GenderSelector from '../components/trainer/GenderSelector';
import AvailabilityTab from '../components/trainer/AvailabilityTab';
import UserSupportTickets from './UserSupportTickets';
import OrderDetailModal from '../components/OrderDetailModal';
import ProductReviewModal from '../components/ProductReviewModal';
import { getBookingTimeDisplay, getTimezoneAbbr } from '../utils/timezoneUtils';
import LegalDisclaimerBox from '../components/LegalDisclaimerBox';
import PaymentInfoTab from '../components/PaymentInfoTab';
import GroupClassesTab from '../components/trainer/GroupClassesTab';

const TrainerDashboard = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [trainerData, setTrainerData] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [loading, setLoading] = useState(true);
  const [pendingFeedback, setPendingFeedback] = useState(null);
  const [showFeedbackBanner, setShowFeedbackBanner] = useState(true);
  // Read tab from URL query params, location state, or default to 'profile'
  const [activeTab, setActiveTab] = useState(
    location.state?.tab || searchParams.get('tab') || 'profile'
  );
  const [userPresences, setUserPresences] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [orderToReview, setOrderToReview] = useState(null);
  const [profileViews, setProfileViews] = useState(0);
  const [showAllBookings, setShowAllBookings] = useState(false);
  const [newReviewsCount, setNewReviewsCount] = useState(0);
  const [warningStatus, setWarningStatus] = useState(null);
  const [creditWarning, setCreditWarning] = useState(null);
  const [showCreditWarningModal, setShowCreditWarningModal] = useState(false);
  
  // Boost package warning states
  const [boostWarning, setBoostWarning] = useState(null);
  const [showBoostWarningModal, setShowBoostWarningModal] = useState(false);
  
  // Track external changes (Gender, Training Types)
  const [genderHasChanges, setGenderHasChanges] = useState(false);
  const [trainingTypesHasChanges, setTrainingTypesHasChanges] = useState(false);
  const externalHasChanges = genderHasChanges || trainingTypesHasChanges;

  // Refs for child components to enable centralized saving
  const genderSelectorRef = useRef(null);
  const trainingTypeSelectorRef = useRef(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Helper function to check for ban in API responses
  const checkBanResponse = async (response) => {
    if (response.status === 403) {
      try {
        const data = await response.clone().json();
        if (data?.detail?.error === 'account_banned') {
          // Force logout and redirect
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('cart_id');
          toast({
            title: 'Account Banned',
            description: data.detail.ban_reason || 'Your account has been banned',
            variant: 'destructive',
          });
          window.location.href = '/login?banned=true';
          return true;
        }
      } catch (e) {
        // Not a JSON response, continue
      }
    }
    return false;
  };

  // Handler for saving all profile-related changes (called from TrainerProfileEditor)
  const handleSaveAllExternal = async () => {
    const results = {
      genderSaved: false,
      trainingTypesSaved: false
    };

    // Save Gender if there are changes
    if (genderSelectorRef.current) {
      try {
        const saved = await genderSelectorRef.current.save();
        results.genderSaved = saved;
        console.log('[TrainerDashboard] Gender save result:', saved);
        if (saved) setGenderHasChanges(false);
      } catch (error) {
        console.error('[TrainerDashboard] Error saving gender:', error);
      }
    }

    // Save Training Types if there are changes
    if (trainingTypeSelectorRef.current) {
      try {
        const saved = await trainingTypeSelectorRef.current.save();
        results.trainingTypesSaved = saved;
        console.log('[TrainerDashboard] Training types save result:', saved);
        if (saved) setTrainingTypesHasChanges(false);
      } catch (error) {
        console.error('[TrainerDashboard] Error saving training types:', error);
      }
    }

    return results;
  };

  // Reset tabs scroll position when component mounts or activeTab changes (mobile fix)
  useEffect(() => {
    // Multiple attempts to reset scroll position
    const resetScroll = () => {
      const tabsList = document.querySelector('.overflow-x-auto.scrollbar-hide');
      if (tabsList && window.innerWidth < 640) {
        tabsList.scrollLeft = 0;
        tabsList.scrollTo(0, 0);
      }
    };
    
    // Try immediately
    resetScroll();
    
    // Try after short delay
    const timer1 = setTimeout(resetScroll, 100);
    const timer2 = setTimeout(resetScroll, 300);
    const timer3 = setTimeout(resetScroll, 600);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [activeTab]);

  // Mark reviews as viewed when Reviews tab is opened
  useEffect(() => {
    if (activeTab === 'reviews') {
      markReviewsAsViewed();
    }
  }, [activeTab]);


  useEffect(() => {
    fetchAllData();
    fetchConversations();
    fetchPendingFeedback();
    fetchWarningStatus();
    fetchCreditWarning();
    fetchBoostWarning();
    fetchRecentOrders();
    fetchProfileViews();
    fetchNewReviewsCount();
    
    // Presence heartbeat - send every 60 seconds
    const sendHeartbeat = async () => {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_BASE}/api/presence/heartbeat`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'online' })
        });
      } catch (error) {
        console.error('Failed to send presence heartbeat:', error);
      }
    };
    
    sendHeartbeat(); // Send immediately
    
    // Set up intervals for real-time updates
    const heartbeatInterval = setInterval(sendHeartbeat, 60000); // Every 60 seconds
    const conversationsInterval = setInterval(fetchConversations, 10000); // Every 10 seconds
    const dataInterval = setInterval(fetchAllData, 30000); // Every 30 seconds - bookings, rating, earnings
    const viewsInterval = setInterval(fetchProfileViews, 30000); // Every 30 seconds - profile views
    const ordersInterval = setInterval(fetchRecentOrders, 30000); // Every 30 seconds - recent orders
    
    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(conversationsInterval);
      clearInterval(dataInterval);
      clearInterval(viewsInterval);
      clearInterval(ordersInterval);
    };
  }, []);

  const fetchPendingFeedback = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get latest notifications for this trainer
      const response = await fetch(`${API_BASE}/api/trainer/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const notifications = await response.json();
        // Find the most recent unread listing_changes_requested notification
        const changeRequest = notifications.find(
          n => n.type === 'listing_changes_requested' && !n.read
        );
        
        if (changeRequest) {
          setPendingFeedback(changeRequest);
        }
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  };

  const handleDismissFeedback = async () => {
    if (!pendingFeedback || !pendingFeedback.id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/notifications/${pendingFeedback.id}/mark-read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setShowFeedbackBanner(false);
        setPendingFeedback(null);
      } else {
        console.error('Failed to mark notification as read');
        // Still hide banner locally even if API fails
        setShowFeedbackBanner(false);
      }
    } catch (error) {
      console.error('Error dismissing feedback:', error);
      // Still hide banner locally even if API fails
      setShowFeedbackBanner(false);
    }
  };

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [bookingsRes, reviewsRes, trainerRes] = await Promise.all([
        fetch(`${API_BASE}/api/trainers/bookings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/api/trainers/reviews`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/api/trainers/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      // Check for ban in any response
      for (const res of [bookingsRes, reviewsRes, trainerRes]) {
        if (await checkBanResponse(res)) return;
      }

      if (bookingsRes.ok) {
        const bookingsData = await bookingsRes.json();
        setBookings(bookingsData);
      }
      if (reviewsRes.ok) {
        const reviewsData = await reviewsRes.json();
        setReviews(reviewsData);
      }
      if (trainerRes.ok) {
        const trainerInfo = await trainerRes.json();
        setTrainerData(trainerInfo);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarningStatus = async () => {
    try {
      console.log('[WARNING] Fetching warning status...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/warning-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('[WARNING] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[WARNING] Data received:', data);
        setWarningStatus(data);
      } else {
        console.error('[WARNING] Response not OK:', response.status);
      }
    } catch (error) {
      console.error('[WARNING] Error fetching warning status:', error);
    }
  };

  const fetchCreditWarning = async () => {
    try {
      console.log('[CREDIT WARNING] Fetching credit status...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/listing-credits/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('[CREDIT WARNING] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[CREDIT WARNING] Data received:', data);
        
        // Check if warning should be shown (7 days or less)
        if (data.show_warning && data.has_active_credit) {
          console.log('[CREDIT WARNING] Warning conditions met!');
          setCreditWarning(data);
          
          // Check if we should show modal (once per day)
          const today = new Date().toDateString();
          const lastShown = localStorage.getItem('creditWarningShown');
          
          console.log('[CREDIT WARNING] Today:', today, 'Last shown:', lastShown);
          
          if (lastShown !== today) {
            console.log('[CREDIT WARNING] Showing modal!');
            setShowCreditWarningModal(true);
            localStorage.setItem('creditWarningShown', today);
          } else {
            console.log('[CREDIT WARNING] Modal already shown today');
          }
        } else {
          console.log('[CREDIT WARNING] No warning needed. show_warning:', data.show_warning, 'has_active_credit:', data.has_active_credit);
        }
      }
    } catch (error) {
      console.error('[CREDIT WARNING] Error fetching credit warning:', error);
    }
  };

  // Fetch boost package warning
  const fetchBoostWarning = async () => {
    try {
      console.log('[BOOST WARNING] Fetching boost status...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/membership`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('[BOOST WARNING] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[BOOST WARNING] Data received:', data);
        
        // Check if warning should be shown (based on backend logic)
        if (data.membership && data.membership.show_warning) {
          console.log('[BOOST WARNING] Warning conditions met!');
          setBoostWarning({
            days_remaining: data.membership.remaining_days,
            end_date: data.membership.end_date,
            package_name: data.membership.details?.name || 'Boost Package'
          });
          
          // Check if we should show modal (once per day)
          const today = new Date().toDateString();
          const lastShown = localStorage.getItem('boostWarningShown');
          
          console.log('[BOOST WARNING] Today:', today, 'Last shown:', lastShown);
          
          if (lastShown !== today) {
            console.log('[BOOST WARNING] Showing modal!');
            setShowBoostWarningModal(true);
            localStorage.setItem('boostWarningShown', today);
          } else {
            console.log('[BOOST WARNING] Modal already shown today');
          }
        } else {
          console.log('[BOOST WARNING] No warning needed');
        }
      }
    } catch (error) {
      console.error('[BOOST WARNING] Error fetching boost warning:', error);
    }
  };

  const fetchNewReviewsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/new-reviews-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewReviewsCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching new reviews count:', error);
    }
  };

  const markReviewsAsViewed = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/api/trainers/mark-reviews-viewed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Reset new reviews count to 0
      setNewReviewsCount(0);
    } catch (error) {
      console.error('Error marking reviews as viewed:', error);
    }
  };



  const fetchRecentOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/orders/user?page=1&limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecentOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching recent orders:', error);
    }
  };

  const fetchProfileViews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/me/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfileViews(data.profile_views || 0);
      }
    } catch (error) {
      console.error('Error fetching profile views:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoadingConversations(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        
        const unread = (data.conversations || []).reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        setUnreadCount(unread);
        
        // Fetch presence status for all conversation participants
        const otherUserIds = (data.conversations || []).map(conv => conv.other_user_id).filter(Boolean);
        if (otherUserIds.length > 0) {
          try {
            const presenceResponse = await fetch(`${API_BASE}/api/presence/bulk`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ user_ids: otherUserIds })
            });
            
            if (presenceResponse.ok) {
              const presenceData = await presenceResponse.json();
              setUserPresences(presenceData.presences || {});
            }
          } catch (presenceError) {
            console.error('Error fetching presence data:', presenceError);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const isPending = user?.status === 'pending';
  const totalEarnings = bookings.filter(b => b.status === 'completed').reduce((sum, b) => sum + (b.price || 0), 0);
  const upcomingBookings = bookings.filter(b => b.status === 'confirmed' || b.status === 'pending');

  const handleBookingAction = async (bookingId, action) => {
    try {
      const token = localStorage.getItem('token');
      
      // Find the booking to get customer info
      const booking = bookings.find(b => b.id === bookingId);
      
      const response = await fetch(`${API_BASE}/api/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: action })
      });

      if (response.ok) {
        fetchAllData(); // Refresh data
        
        // If booking was accepted, start a conversation with the customer and switch to Messages tab
        if (action === 'confirmed' && booking) {
          try {
            // Get the customer's user_id to start conversation
            const conversationResponse = await fetch(`${API_BASE}/api/messages/conversations`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                participant_id: booking.customer_user_id
              })
            });
            
            if (conversationResponse.ok) {
              const conversationData = await conversationResponse.json();
              
              // Refresh conversations list
              await fetchConversations();
              
              // Give a small delay for state to update
              setTimeout(() => {
                // Switch to Messages tab and select this conversation
                setActiveTab('messages');
                setSelectedConversationId(conversationData.conversation_id);
                
                // Show success toast
                toast({
                  title: "Booking Confirmed!",
                  description: `Conversation started with ${booking.user_name}. You can now message them.`,
                  duration: 5000,
                });
              }, 500);
              
              console.log(`✅ Booking confirmed! Conversation started with ${booking.user_name}`);
            } else {
              const errorData = await conversationResponse.json();
              console.error('Failed to create conversation:', errorData);
              toast({
                title: "Booking Confirmed",
                description: "Booking accepted but couldn't start conversation. Please try messaging from Messages tab.",
                variant: "destructive",
                duration: 5000,
              });
            }
          } catch (convError) {
            console.error('Error starting conversation:', convError);
            // Still accepted the booking, just show warning
            toast({
              title: "Booking Confirmed",
              description: "Booking accepted successfully!",
              duration: 3000,
            });
          }
        }
      }
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 lg:p-6 overflow-x-hidden">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-base sm:text-2xl lg:text-3xl font-bold">Trainer Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-xs sm:text-sm lg:text-base">Manage your profile, bookings, and clients</p>
        </div>

        {/* Pending Approval Alert */}
        {isPending && (
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertDescription className="flex items-center gap-2 text-sm sm:text-base">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 flex-shrink-0" />
              <span className="text-yellow-800">
                Your trainer account is <strong>pending approval</strong>. You will be notified once approved by the admin team.
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Warning Banner for 3+ Reviewed Reports */}
        {console.log('[WARNING] Rendering check - warningStatus:', warningStatus, 'show_warning:', warningStatus?.show_warning)}
        {warningStatus?.show_warning && (
          <Alert className="border-orange-300 bg-gradient-to-r from-red-50 via-orange-50 to-yellow-50 border-2">
            <AlertDescription className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 text-2xl">⚠️</div>
                <div className="flex-1 space-y-2">
                  <p className="font-semibold text-gray-900 text-base">
                    Dear Trainer,
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    In the last 30 days, <strong>{warningStatus.report_count} reports</strong> have been received about your profile and reviewed by our moderation team. 
                    Your success is important to us as part of our community.
                  </p>
                  <div className="bg-white dark:bg-gray-800/60 p-3 rounded-lg border border-orange-200 space-y-2">
                    <p className="text-sm font-medium text-gray-800">💡 To regain trust:</p>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Maintain professional and courteous communication with clients</li>
                      <li>• Pay attention to service quality and keep your promises</li>
                      <li>• Follow platform guidelines</li>
                    </ul>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex-shrink-0 text-green-600">✅</div>
                    <p className="text-gray-600 dark:text-gray-400">
                      <strong>Good news:</strong> If you don't receive any new reports within {warningStatus.days_until_auto_dismiss} days, 
                      this warning will be automatically removed. We wish you continued success!
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 italic mt-2">
                    - TrainerList Team 💪
                  </p>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-4">
          <Card>
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex flex-col items-center text-center gap-2">
                <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
                <p className="text-xs sm:text-sm text-gray-600">Upcoming</p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold">{upcomingBookings.length}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex flex-col items-center text-center gap-2">
                <DollarSign className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
                <p className="text-xs sm:text-sm text-gray-600">Earnings</p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold">${totalEarnings}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex flex-col items-center text-center gap-2">
                <Star className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-500" />
                <p className="text-xs sm:text-sm text-gray-600">Rating</p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold">{trainerData?.rating?.toFixed(1) || '0.0'}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex flex-col items-center text-center gap-2">
                <Eye className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-600" />
                <p className="text-xs sm:text-sm text-gray-600">Profile Views</p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold">{profileViews}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex flex-col items-center text-center gap-2">
                <MessageCircle className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" />
                <p className="text-xs sm:text-sm text-gray-600">Messages</p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold">{unreadCount}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin Feedback Banner */}
        {pendingFeedback && showFeedbackBanner && (
          <Alert className="mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-300 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="text-2xl">📝</span>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-orange-900 mb-2">
                  Admin Feedback: Profile Updates Needed
                </h3>
                <AlertDescription className="text-orange-800 space-y-3">
                  <p className="font-semibold">
                    Our admin team has reviewed your profile and requests the following updates before listing approval:
                  </p>
                  
                  {pendingFeedback.missing_items && pendingFeedback.missing_items.length > 0 && (
                    <div className="bg-white dark:bg-gray-800/50 p-3 rounded-lg border border-orange-200">
                      <p className="font-semibold text-orange-900 mb-2">Missing or Incomplete Items:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {pendingFeedback.missing_items.map((item) => (
                          <li key={item} className="text-orange-800">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {pendingFeedback.additional_notes && (
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="font-semibold text-blue-900 mb-1">Additional Notes from Admin:</p>
                      <p className="text-blue-800 whitespace-pre-wrap">{pendingFeedback.additional_notes}</p>
                    </div>
                  )}
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => setActiveTab('profile')}
                      className="bg-orange-600 hover:bg-orange-700 text-white font-semibold"
                    >
                      ✏️ Edit Profile
                    </Button>
                    <Button
                      onClick={handleDismissFeedback}
                      variant="outline"
                      className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    >
                      Dismiss
                    </Button>
                  </div>
                </AlertDescription>
              </div>
            </div>
          </Alert>
        )}

        {/* Profile Completion Alert */}
        {trainerData && (!trainerData.bio || !trainerData.photos || trainerData.photos.length === 0 || !trainerData.certificates || trainerData.certificates.length === 0) && (
          <Alert className="bg-gradient-to-r from-red-50 to-gray-50 border-red-200">
            <User className="h-5 w-5 text-red-600" />
            <AlertDescription className="ml-2">
              <span className="font-semibold text-red-900">Complete your profile to attract more clients!</span>
              <br />
              <span className="text-sm text-red-700">
                {!trainerData.bio && "Add your bio • "}
                {(!trainerData.photos || trainerData.photos.length === 0) && (
                  <button
                    onClick={() => {
                      setActiveTab('profile');
                      setTimeout(() => {
                        const element = document.getElementById('training-photos-section');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          element.classList.add('ring-2', 'ring-red-400', 'ring-offset-2');
                          setTimeout(() => element.classList.remove('ring-2', 'ring-red-400', 'ring-offset-2'), 2000);
                        }
                      }, 300);
                    }}
                    className="text-red-700 hover:text-red-900 underline underline-offset-2 cursor-pointer transition-colors"
                  >
                    Upload training photos
                  </button>
                )}
                {(!trainerData.photos || trainerData.photos.length === 0) && (!trainerData.certificates || trainerData.certificates.length === 0) && " • "}
                {(!trainerData.certificates || trainerData.certificates.length === 0) && (
                  <button
                    onClick={() => {
                      setActiveTab('profile');
                      setTimeout(() => {
                        const element = document.getElementById('certifications-section');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          element.classList.add('ring-2', 'ring-red-400', 'ring-offset-2');
                          setTimeout(() => element.classList.remove('ring-2', 'ring-red-400', 'ring-offset-2'), 2000);
                        }
                      }, 300);
                    }}
                    className="text-red-700 hover:text-red-900 underline underline-offset-2 cursor-pointer transition-colors"
                  >
                    Add certifications
                  </button>
                )}
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Payment Legal Disclaimer Notice - Dynamic from API */}
        <LegalDisclaimerBox variant="compact" className="mb-6" />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Tab Navigation - Split into rows */}
          <div className="space-y-2">
            {/* Row 0: Payment Info Tab - Highlighted with Red Border */}
            <TabsList className="w-full h-auto bg-transparent p-0">
              <TabsTrigger 
                value="payment-info" 
                className="w-full sm:w-auto text-sm sm:text-base py-3 px-4 font-bold border-4 border-red-500 bg-red-100 dark:from-red-950/40 dark:to-orange-950/40 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/40 data-[state=active]:bg-red-500 data-[state=active]:text-white data-[state=active]:border-red-600 rounded-lg shadow-md transition-all"
              >
                <DollarSign className="w-5 h-5 mr-2 inline-block" />
                <AlertTriangle className="w-4 h-4 mr-2 inline-block animate-pulse" />
                <span>Payment Information</span>
              </TabsTrigger>
            </TabsList>

            {/* Row 1: Main Tabs - Profile, Schedule & Communication */}
            <TabsList className="grid grid-cols-4 sm:grid-cols-7 w-full h-auto gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
              <TabsTrigger value="profile" className="text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5 font-semibold">
                <User className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Profile</span>
                <span className="hidden sm:inline">My Profile</span>
              </TabsTrigger>
              <TabsTrigger value="availability" className="text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <CalendarClock className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Hours</span>
                <span className="hidden sm:inline">Availability</span>
              </TabsTrigger>
              <TabsTrigger value="bookings" className="relative text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Book</span>
                <span className="hidden sm:inline">Bookings</span>
                {bookings.filter(b => b.status === 'pending').length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {bookings.filter(b => b.status === 'pending').length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="reviews" className="relative text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <Star className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Rev</span>
                <span className="hidden sm:inline">Reviews</span>
                {newReviewsCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {newReviewsCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="messages" className="relative text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Msg</span>
                <span className="hidden sm:inline">Messages</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="group-classes" className="text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <Video className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Class</span>
                <span className="hidden sm:inline">Group Classes</span>
              </TabsTrigger>
              <TabsTrigger 
                value="support" 
                className="support-tickets-tab relative text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5 font-bold"
              >
                <LifeBuoy className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Help</span>
                <span className="hidden sm:inline">Support</span>
                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center shadow-lg">
                  ?
                </span>
              </TabsTrigger>
            </TabsList>

            {/* Row 2: Management Tabs - Purchases, Credits & Settings */}
            <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full h-auto gap-1 bg-gray-50 dark:bg-gray-800/50 p-1 rounded-lg border border-gray-200 dark:border-gray-700">
              <TabsTrigger value="orders" className="text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Orders</span>
                <span className="hidden sm:inline">My Orders</span>
              </TabsTrigger>
              <TabsTrigger value="membership" className="text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <Crown className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Boost</span>
                <span className="hidden sm:inline">Boost Package</span>
              </TabsTrigger>
              <TabsTrigger value="listing-credits" className="text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Credits</span>
                <span className="hidden sm:inline">Listing Credits</span>
              </TabsTrigger>
              <TabsTrigger value="achievements" className="text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <Trophy className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Badges</span>
                <span className="hidden sm:inline">Achievements</span>
              </TabsTrigger>
              <TabsTrigger value="referral" className="text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <Gift className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Refer</span>
                <span className="hidden sm:inline">Referral</span>
              </TabsTrigger>
              <TabsTrigger value="social-media" className="text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <Globe className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Social</span>
                <span className="hidden sm:inline">Social Media</span>
              </TabsTrigger>
              <TabsTrigger value="account" className="text-[10px] sm:text-sm py-2 px-1 sm:px-3 sm:py-2.5">
                <Settings className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 inline-block" />
                <span className="sm:hidden">Acc</span>
                <span className="hidden sm:inline">Account</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Payment Info Tab Content - First Tab */}
          <TabsContent value="payment-info" className="space-y-4 mt-4">
            <PaymentInfoTab />
          </TabsContent>

          {/* Profile Tab - First and Default */}
          <TabsContent value="profile" className="space-y-4 mt-4">
            <TrainerProfileEditor 
              onSaveAll={handleSaveAllExternal} 
              externalHasChanges={externalHasChanges}
            />
            <GenderSelector 
              ref={genderSelectorRef} 
              onChangeStatus={setGenderHasChanges}
            />
            <TrainingTypeSelector 
              ref={trainingTypeSelectorRef} 
              onChangeStatus={setTrainingTypesHasChanges}
            />
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability" className="space-y-4 mt-4">
            <AvailabilityTab />
          </TabsContent>

          <TabsContent value="bookings" className="space-y-4 mt-4">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-row items-center justify-between">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Upcoming Sessions</h3>
                <Button
                  onClick={() => setShowAllBookings(!showAllBookings)}
                  variant="outline"
                  size="sm"
                  className="text-xs sm:text-sm dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {showAllBookings ? 'Show Upcoming Only' : 'View All Sessions'}
                </Button>
              </div>
              <div className="p-6 space-y-3 sm:space-y-4">
                {(showAllBookings ? bookings : upcomingBookings).map((booking) => (
                  <div key={booking.id} className="flex flex-col p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg gap-3 bg-white dark:bg-gray-800/50">
                    {/* Customer Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                          <AvatarFallback className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{booking.user_name ? booking.user_name[0].toUpperCase() : 'U'}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <h4 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">{booking.user_name || 'Client'}</h4>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{booking.specialization}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={
                          booking.status === 'confirmed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 
                          booking.status === 'cancelled' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                          'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        }>
                          {booking.status}
                        </Badge>
                        <p className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">${booking.price}</p>
                      </div>
                    </div>

                    {/* Session Details */}
                    <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-2 rounded border border-gray-200 dark:border-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                        {booking.date}
                      </span>
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-green-100 dark:bg-green-900/40 rounded-md border border-green-200 dark:border-green-700">
                        <Clock className="w-3 h-3 text-green-600 dark:text-green-400" />
                        <span className="text-xs font-medium text-green-700 dark:text-green-300">
                          Your time: {booking.time} ({booking.duration} min)
                        </span>
                      </div>
                      {/* Customer's Local Time - More Visible Display */}
                      {booking.user_timezone && booking.user_timezone !== user?.timezone && (
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 dark:bg-blue-900/40 rounded-md border border-blue-200 dark:border-blue-700">
                          <Globe className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                          <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                            Customer's time: {getBookingTimeDisplay(booking, user?.timezone, booking.user_timezone).convertedTime} {getTimezoneAbbr(booking.user_timezone)}
                          </span>
                        </div>
                      )}
                      {booking.course_type && (
                        <Badge variant="outline" className="text-xs dark:border-gray-600 dark:text-gray-300">
                          {booking.course_type === 'online' ? '🌐 Online' : '🏋️ In Person'}
                        </Badge>
                      )}
                    </div>

                    {/* Customer Contact Info */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg space-y-1 text-xs sm:text-sm border border-blue-200 dark:border-blue-700">
                      <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2">📞 Contact Information</p>
                      {booking.customer_email && (
                        <p className="text-gray-700 dark:text-gray-300">
                          <span className="font-medium">Email:</span> {booking.customer_email}
                        </p>
                      )}
                      {booking.customer_phone && (
                        <p className="text-gray-700 dark:text-gray-300">
                          <span className="font-medium">Phone:</span> {booking.customer_phone}
                        </p>
                      )}
                    </div>

                    {/* Customer Message */}
                    {booking.customer_message && (
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-700">
                        <p className="font-semibold text-gray-700 dark:text-gray-300 text-xs sm:text-sm mb-1">💬 Customer Message</p>
                        <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{booking.customer_message}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {booking.status === 'pending' && (
                      <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1 text-xs dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          onClick={() => handleBookingAction(booking.id, 'cancelled')}
                        >
                          Decline
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1 text-xs bg-green-600 hover:bg-green-700"
                          onClick={() => handleBookingAction(booking.id, 'confirmed')}
                        >
                          Accept Booking
                        </Button>
                      </div>
                    )}
                  </div>
                ))}

                {upcomingBookings.length === 0 && (
                  <div className="text-center py-6 sm:py-8 text-gray-500 dark:text-gray-400">
                    <Calendar className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
                    <p className="text-sm sm:text-base">No upcoming bookings</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Client Reviews</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b last:border-b-0 pb-4 last:pb-0">
                    <div className="flex items-start gap-3">
                      <Avatar className="flex-shrink-0">
                        <AvatarFallback>{review.user_name ? review.user_name.charAt(0) : 'U'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-1 gap-1">
                          <h4 className="font-semibold text-sm sm:text-base truncate">{review.user_name || 'User'}</h4>
                          <span className="text-xs sm:text-sm text-gray-500">{review.date}</span>
                        </div>
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={`star-${i}`}
                              className={`w-3 h-3 sm:w-4 sm:h-4 ${
                                i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm">{review.comment}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {reviews.length === 0 && (
                  <div className="text-center py-6 sm:py-8 text-gray-500">
                    <Star className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-sm sm:text-base">No reviews yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="orders" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg sm:text-xl">My Orders</CardTitle>
                <Link to="/orders" className="text-sm text-red-600 hover:text-red-700 font-medium">
                  View All →
                </Link>
              </CardHeader>
              <CardContent className="space-y-3">
                {recentOrders.map((order) => (
                  <div key={order.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-4 h-4 text-red-600" />
                          <span className="font-semibold text-sm">Order #{order.order_number}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            order.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                            order.order_status === 'shipped' ? 'bg-red-100 text-red-800' :
                            order.order_status === 'processing' ? 'bg-gray-100 text-gray-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {order.order_status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {order.items?.length || 0} item(s) • {new Date(order.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                        {order.tracking_number && (
                          <p className="text-xs text-gray-500 mt-1">
                            Tracking: {order.tracking_number}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-600">
                          ${parseFloat(order.total || 0).toFixed(2)}
                        </p>
                        <div className="flex gap-2 mt-1">
                          <button 
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowOrderModal(true);
                            }}
                            className="text-xs text-red-600 hover:underline cursor-pointer"
                          >
                            Details
                          </button>
                          
                          {order.order_status === 'delivered' && (
                            <>
                              <span className="text-gray-300">|</span>
                              
                              {/* Deleted by admin - show disabled state */}
                              {order.review_status === 'deleted' && (
                                <span 
                                  className="text-xs text-gray-400 cursor-not-allowed flex items-center gap-1"
                                  title="Your review was removed by admin. You cannot review this order again."
                                >
                                  <Star className="w-3 h-3" />
                                  Review Removed
                                </span>
                              )}
                              
                              {/* Show Write Review for no review, pending, or rejected */}
                              {(order.review_status === 'none' || order.review_status === 'pending' || order.review_status === 'rejected') && (
                                <button 
                                  onClick={() => {
                                    setOrderToReview(order);
                                    setShowReviewModal(true);
                                  }}
                                  className="text-xs text-green-600 hover:underline cursor-pointer flex items-center gap-1"
                                >
                                  <Star className="w-3 h-3" />
                                  {order.review_status === 'pending' ? 'Edit Review' : order.review_status === 'rejected' ? 'Resubmit Review' : 'Write Review'}
                                </button>
                              )}
                              
                              {/* Approved review */}
                              {order.review_status === 'approved' && (
                                <span className="text-xs text-gray-500">✓ Reviewed</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {recentOrders.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-sm">No orders yet</p>
                    <Link to="/shop" className="text-sm text-red-600 hover:underline mt-2 inline-block">
                      Browse Shop →
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="messages" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
              <div className="lg:col-span-1 order-2 lg:order-1">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base sm:text-lg">Conversations</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-[400px] lg:max-h-[600px] overflow-y-auto">
                      <ConversationList
                        conversations={conversations}
                        selectedId={selectedConversationId}
                        onSelect={setSelectedConversationId}
                        currentUserId={user?.id}
                        userPresences={userPresences}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-2 order-1 lg:order-2">
                <Card className="h-[500px] sm:h-[600px]">
                  {selectedConversationId ? (
                    <MessageThread
                      conversationId={selectedConversationId}
                      currentUserId={user?.id}
                      onBack={() => setSelectedConversationId(null)}
                    />
                  ) : (
                    <CardContent className="flex items-center justify-center h-full p-2">
                      <div className="text-center text-gray-500">
                        <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-xs font-medium">Select a conversation to start messaging</p>
                        <p className="text-[10px] mt-1 text-gray-400">Choose from your conversations on the left</p>
                      </div>
                    </CardContent>
                  )}
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Membership Tab */}
          <TabsContent value="membership" className="space-y-4 mt-4">
            <MembershipTab onNavigateToListingCredits={() => setActiveTab('listing-credits')} />
          </TabsContent>

          {/* Listing Credits Tab */}
          <TabsContent value="listing-credits" className="space-y-4 mt-4">
            <ListingCreditsTab />
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-4 mt-4">
            <AchievementsTab />
          </TabsContent>

          {/* Referral Tab */}
          <TabsContent value="referral" className="space-y-4 mt-4">
            <ReferralTab />
          </TabsContent>

          {/* Social Media Tab */}
          <TabsContent value="social-media" className="space-y-4 mt-4">
            <SocialMediaTab />
          </TabsContent>

          {/* Group Classes Tab */}
          <TabsContent value="group-classes" className="space-y-4 mt-4">
            <GroupClassesTab />
          </TabsContent>

          {/* Support Tickets Tab */}
          <TabsContent value="support" className="space-y-4 mt-4">
            <UserSupportTickets />
          </TabsContent>

          {/* Account Settings Tab */}
          <TabsContent value="account" className="space-y-4 mt-4">
            <TrainerAccountSettings trainer={trainerData} onUpdate={fetchAllData} standalone={false} />
          </TabsContent>

        </Tabs>
      </div>

      {/* Order Detail Modal */}
      <OrderDetailModal 
        order={selectedOrder}
        isOpen={showOrderModal}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedOrder(null);
        }}
      />

      {/* Product Review Modal */}
      <ProductReviewModal 
        order={orderToReview}
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setOrderToReview(null);
        }}
        onSuccess={() => {
          fetchRecentOrders();
        }}
      />

      {/* Credit Warning Modal */}
      <Dialog open={showCreditWarningModal} onOpenChange={setShowCreditWarningModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-center flex items-center justify-center gap-2">
              <Clock className="w-6 h-6 text-amber-600" />
              <span>Listing Credit Expiring Soon</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-lg text-amber-900 mb-2">
                {creditWarning?.days_remaining} {creditWarning?.days_remaining === 1 ? 'Day' : 'Days'} Remaining
              </h3>
              <p className="text-sm text-amber-800">
                Your listing credit will expire on <strong>{creditWarning && new Date(creditWarning.end_date).toLocaleDateString()}</strong>.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900 mb-2">
                <strong>💡 What happens when it expires:</strong>
              </p>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Your profile will be removed from the homepage</li>
                <li>Clients won't be able to find you in listings</li>
                <li>Your bookings may decrease significantly</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-900 mb-2">
                <strong>✅ Renew now to stay visible:</strong>
              </p>
              <p className="text-sm text-green-800">
                Purchase a new listing credit package to continue appearing on the homepage and attracting new clients.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCreditWarningModal(false)}
                className="flex-1"
              >
                Remind Me Later
              </Button>
              <Button
                onClick={() => {
                  setShowCreditWarningModal(false);
                  setActiveTab('listing-credits');
                }}
                className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
              >
                Renew Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Boost Package Warning Modal */}
      <Dialog open={showBoostWarningModal} onOpenChange={setShowBoostWarningModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-center flex items-center justify-center gap-2">
              <Zap className="w-6 h-6 text-purple-600" />
              <span>Boost Package Expiring Soon</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-lg text-purple-900 mb-2">
                {boostWarning?.days_remaining} {boostWarning?.days_remaining === 1 ? 'Day' : 'Days'} Remaining
              </h3>
              <p className="text-sm text-purple-800">
                Your <strong>{boostWarning?.package_name}</strong> will expire on <strong>{boostWarning && new Date(boostWarning.end_date).toLocaleDateString()}</strong>.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900 mb-2">
                <strong>💡 What happens when it expires:</strong>
              </p>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>You'll lose your priority placement in search results</li>
                <li>Your profile badge will be removed</li>
                <li>You may see fewer profile views and inquiries</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-900 mb-2">
                <strong>✅ Renew now to keep your boost:</strong>
              </p>
              <p className="text-sm text-green-800">
                Renew your boost package to continue getting priority visibility and attract more clients.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBoostWarningModal(false)}
                className="flex-1"
              >
                Remind Me Later
              </Button>
              <Button
                onClick={() => {
                  setShowBoostWarningModal(false);
                  setActiveTab('membership');
                }}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                Renew Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default TrainerDashboard;

// Custom CSS for Support Tickets tab styling
const style = document.createElement('style');
style.innerHTML = `
  .support-tickets-tab {
    background: linear-gradient(to right, #fee2e2, #f3f4f6) !important;
    color: #dc2626 !important;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1) !important;
    border: 2px solid transparent !important;
  }
  
  .support-tickets-tab:hover {
    background: linear-gradient(to right, #fecaca, #e5e7eb) !important;
    transform: scale(1.02);
  }
  
  .support-tickets-tab[data-state="active"] {
    background: linear-gradient(to right, #dc2626, #000000) !important;
    color: #ffffff !important;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
    border: 2px solid #dc2626 !important;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}
